import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

// 🎨 GLOBAL COLORS DEFINITION 
const colors = {
    primary: '#6366F1', // Indigo/Violet
    secondary: '#8B5CF6', // Purple/Pink
    accent: '#06B6D4', // Cyan
    success: '#10B981', // Green
    warning: '#F59E0B', // Orange
    danger: '#EF4444', // Red
    dark: '#1E293B',
    light: '#F8FAFC',
    // Backgrounds and Borders
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.7)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    // Gradients
    gradientMain: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
};

function ModifyManagers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Password Modal States
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [updating, setUpdating] = useState(false);
    
    // Delete States (ADDED)
    const [deletingUser, setDeletingUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const [togglingStatus, setTogglingStatus] = useState(false);


    // Fetch managers when component mounts
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError('');
            // Fetch users from the 'managers' table
            const response = await fetch(`${API_BASE}/get_users.php?type=managers`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                // Normalize data structure for safety
                const sanitizedUsers = result.data.map(user => ({
                    ...user,
                    description: user.description || user.client_description || 'N/A', 
                    social_media_profile: user.social_media_profile || 'N/A'
                }));
                setUsers(sanitizedUsers);
            } else {
                setError(result.message || 'Failed to fetch managers');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setError('Cannot connect to server. Please make sure your PHP server is running.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // ✅ ADDED: Delete user function
    const handleDeleteUser = async (user) => {
        console.log('🗑️ Delete manager clicked:', user);
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    // ✅ ADDED: Confirm and execute delete
    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            setDeletingUser(userToDelete.id);
            setSuccessMessage('');
            setError('');
            
            console.log('🔄 Deleting manager:', userToDelete.id, 'Type: managers');
            
            const response = await fetch(`${API_BASE}/deleteuser.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userToDelete.id,
                    user_type: 'managers'
                })
            });

            const data = await response.json();
            console.log('📥 Delete response:', data);
            
            if (data.success) {
                setSuccessMessage(`✅ Manager "${userToDelete.name}" deleted successfully!`);
                
                // Update local state by removing the deleted user
                setUsers(prevUsers => prevUsers.filter(user => user.id !== userToDelete.id));
                
                // Close modal
                setShowDeleteModal(false);
                setUserToDelete(null);
                
                // Clear message after 3 seconds
                setTimeout(() => {
                    setSuccessMessage('');
                }, 3000);
            } else {
                throw new Error(data.message || 'Failed to delete manager');
            }
        } catch (error) {
            console.error('❌ Error deleting manager:', error);
            setError(`❌ Error: ${error.message}`);
        } finally {
            setDeletingUser(null);
        }
    };

    const handleEditPassword = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setShowPasswordModal(true);
        setError(''); // Clear error before showing modal
    };

    const handleUpdatePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        try {
            setUpdating(true);
            const response = await fetch(`${API_BASE}/update_password.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userType: 'managers',
                    userId: selectedUser.id,
                    newPassword: newPassword
                })
            });

            const result = await response.json();

            if (result.success) {
                setShowPasswordModal(false);
                setSelectedUser(null);
                setNewPassword('');
                await fetchUsers();
                setSuccessMessage(`Password for ${selectedUser.name} updated successfully!`);
            } else {
                setError(result.message || 'Failed to update password');
            }
        } catch (error) {
            console.error('Update error:', error);
            setError('Cannot connect to server. Please make sure your PHP server is running.');
        } finally {
            setUpdating(false);
        }
    };

    const handleToggleStatus = async (user) => {
        try {
            setTogglingStatus(true);
            const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
            
            const response = await fetch(`${API_BASE}/update_status.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userType: 'managers',
                    userId: user.id,
                    newStatus: newStatus
                })
            });

            const result = await response.json();

            if (result.success) {
                setUsers(prevUsers => 
                    prevUsers.map(u => 
                        u.id === user.id ? { ...u, status: newStatus } : u
                    )
                );
            } else {
                setError(result.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Status update error:', error);
            setError('Cannot connect to server. Please make sure your PHP server is running.');
        } finally {
            setTogglingStatus(false);
        }
    };

    /* --- STYLING FUNCTIONS --- */

    const getStatusBadgeStyle = (status) => {
        const baseStyle = {
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '0.75rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        };

        if (status === 'Active') {
            return {
                ...baseStyle,
                background: colors.success,
                color: 'white'
            };
        } else {
            return {
                ...baseStyle,
                background: '#6B7280',
                color: 'white'
            };
        }
    };

    const getActionButtonStyle = (type) => {
        const baseStyle = {
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.3s ease'
        };

        switch (type) {
            case 'password':
                return {
                    ...baseStyle,
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: colors.primary,
                    border: `1px solid rgba(99, 102, 241, 0.3)`
                };
            case 'status':
                return {
                    ...baseStyle,
                    background: colors.warning,
                    color: colors.dark,
                    border: `1px solid ${colors.warning}`,
                };
            case 'delete': // ✅ ADDED: Delete button style
                return {
                    ...baseStyle,
                    background: `linear-gradient(135deg, ${colors.danger}, #dc2626)`,
                    color: 'white',
                    border: `1px solid ${colors.danger}`,
                };
            default:
                return baseStyle;
        }
    };

    return (
        <Layout>
            <div className="modify-editors-container" style={styles.container}>
                {/* Animated Background */}
                <div className="animated-background" style={styles.animatedBackground}>
                    <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                    <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                    <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                    <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                    <div className="grid-overlay" style={styles.gridOverlay}></div>
                </div>

                {/* Header Section */}
                <div className="editors-header" style={styles.header}>
                    <div className="header-content" style={styles.headerContent}>
                        <div className="welcome-section">
                            <div className="greeting" style={styles.greeting}>
                                <h1 className="page-title" style={styles.pageTitle}>
                                    <i className="fas fa-user-tie me-2"></i>
                                    Managers Management
                                </h1>
                                <div className="welcome-badge" style={styles.welcomeBadge}>
                                    <span className="badge-text" style={styles.badgeText}>Admin Panel</span>
                                    <span className="badge-role" style={styles.badgeRole}>Manager</span>
                                </div>
                            </div>
                            <p className="page-subtitle" style={styles.pageSubtitle}>
                                Manage and monitor all manager accounts and their status
                            </p>
                        </div>
                        <div className="header-actions" style={styles.headerActions}>
                            <div className="client-count" style={styles.clientCount}>
                                <span style={styles.clientCountText}>Total Managers:</span>
                                <span style={styles.clientCountNumber}>{users.length}</span>
                            </div>
                            <button 
                                className="refresh-btn"
                                onClick={() => fetchUsers()} 
                                disabled={loading}
                                style={styles.refreshBtn}
                            >
                                <div className="refresh-icon" style={styles.refreshIcon}>
                                    <i className={`fas fa-sync ${loading ? 'spinning' : ''}`}></i>
                                </div>
                                <span>{loading ? 'Fetching...' : 'Refresh Data'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Success Message for Profile Update */}
                {successMessage && (
                    <div className="success-alert" style={styles.successAlert}>
                        <div className="alert-icon" style={{...styles.alertIcon, color: colors.success}}>
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="alert-message" style={styles.alertMessage}>
                            <strong>Success</strong>
                            <span>{successMessage}</span>
                        </div>
                        <button className="alert-close" onClick={() => setSuccessMessage('')} style={{...styles.alertClose, color: colors.success}}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}
                
                {/* Error Message */}
                {error && (
                    <div className="error-alert" style={styles.errorAlert}>
                        <div className="alert-icon" style={styles.alertIcon}>
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <div className="alert-message" style={styles.alertMessage}>
                            <strong>Attention Needed</strong>
                            <span>{error}</span>
                        </div>
                        <button className="alert-close" onClick={() => setError('')} style={styles.alertClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}

                {/* Main Content */}
                <div className="editors-content" style={styles.content}>
                    {/* Editors Table Section */}
                    <div className="editors-table-section" style={styles.clientsTableSection}>
                        <div className="table-card" style={styles.tableCard}>
                            <div className="table-card-header" style={styles.tableCardHeader}>
                                <h3 style={styles.tableCardTitle}>
                                    <i className="fas fa-table me-2"></i>
                                    Managers Records
                                </h3>
                            </div>
                            <div className="table-card-body" style={styles.tableCardBody}>
                                {loading ? (
                                    <div className="loading-state" style={styles.loadingState}>
                                        <div className="loading-animation" style={styles.loadingAnimation}>
                                            <div className="loading-dots" style={styles.loadingDots}>
                                                <div className="dot" style={styles.dot}></div>
                                                <div className="dot" style={styles.dot}></div>
                                                <div className="dot" style={styles.dot}></div>
                                            </div>
                                        </div>
                                        <h3 style={styles.loadingText}>Loading Managers</h3>
                                        <p style={styles.loadingSubtext}>Fetching manager data...</p>
                                    </div>
                                ) : users.length === 0 ? (
                                    <div className="empty-state" style={styles.emptyState}>
                                        <div className="empty-icon" style={styles.emptyIcon}>
                                            <i className="fas fa-users"></i>
                                        </div>
                                        <h3 style={styles.emptyTitle}>No Managers Found</h3>
                                        <p style={styles.emptyText}>Managers will appear here when added</p>
                                    </div>
                                ) : (
                                    <div className="table-container" style={styles.tableContainer}>
                                        <table className="editors-table" style={styles.clientsTable}>
                                            <thead>
                                                <tr>
                                                    {/* Final simplified table headers */}
                                                    {['ID', 'Full Name', 'Email Address', 'Status', 'Actions'].map((header, index) => (
                                                        <th key={index} style={styles.tableHeader}>
                                                            {header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map(user => (
                                                    <tr key={user.id} style={styles.tableRow}>
                                                        <td style={styles.tableCell}>
                                                            <span style={styles.userId}>#{user.id}</span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <div className="user-info" style={styles.userInfo}>
                                                                <div className="user-avatar" style={styles.userAvatar}>
                                                                    <i className="fas fa-user"></i>
                                                                </div>
                                                                <div className="user-details" style={styles.userDetails}>
                                                                    <div className="user-name" style={styles.userName}>{user.name}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span style={styles.userEmail}>{user.email}</span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span className="status-badge" style={getStatusBadgeStyle(user.status)}>
                                                                <i className={`fas ${user.status === 'Active' ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                                                                {user.status}
                                                            </span>
                                                        </td>
                                                        
                                                        <td style={styles.tableCell}>
                                                            <div className="action-buttons" style={{...styles.actionButtons, flexWrap: 'wrap', gap: '6px'}}>
                                                                {/* PASSWORD RESET BUTTON */}
                                                                <button
                                                                    className="password-btn"
                                                                    onClick={() => handleEditPassword(user)}
                                                                    style={getActionButtonStyle('password')}
                                                                >
                                                                    <i className="fas fa-key me-1"></i>
                                                                    Password
                                                                </button>
                                                                
                                                                {/* STATUS TOGGLE BUTTON */}
                                                                <button
                                                                    className="status-btn"
                                                                    onClick={() => handleToggleStatus(user)}
                                                                    disabled={togglingStatus}
                                                                    style={{
                                                                        ...getActionButtonStyle('status'),
                                                                        opacity: togglingStatus ? 0.6 : 1,
                                                                        cursor: togglingStatus ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                >
                                                                    {togglingStatus ? (
                                                                        <span className="spinner" style={styles.spinner}></span>
                                                                    ) : (
                                                                        <i className={`fas ${
                                                                            user.status === 'Active' ? 'fa-toggle-on' : 'fa-toggle-off'
                                                                        } me-1`}></i>
                                                                    )}
                                                                    {user.status === 'Active' ? 'Deactivate' : 'Activate'}
                                                                </button>

                                                                {/* ✅ ADDED: Delete Button */}
                                                                <button
                                                                    className="delete-btn"
                                                                    onClick={() => handleDeleteUser(user)}
                                                                    disabled={deletingUser === user.id}
                                                                    style={{
                                                                        ...getActionButtonStyle('delete'),
                                                                        opacity: deletingUser === user.id ? 0.6 : 1,
                                                                        cursor: deletingUser === user.id ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                    title="Delete manager"
                                                                >
                                                                    {deletingUser === user.id ? (
                                                                        <>
                                                                            <span className="spinner" style={styles.spinner}></span>
                                                                            Deleting...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="fas fa-trash me-1"></i>
                                                                            Delete
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            {users.length > 0 && (
                                <div className="table-footer" style={styles.tableFooter}>
                                    <div className="footer-content" style={styles.footerContent}>
                                        <span style={styles.footerText}>
                                            Showing <strong>{users.length}</strong> manager records
                                        </span>
                                        <span style={styles.footerText}>
                                            Last updated: {new Date().toLocaleTimeString()}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ✅ ADDED: Delete Confirmation Modal */}
                {showDeleteModal && userToDelete && (
                    <div className="modal-overlay" style={styles.modalOverlay}>
                        <div className="modal-container" style={styles.modalContainer}>
                            <div className="modal-card" style={styles.modalCard}>
                                <div className="modal-header" style={styles.modalHeader}>
                                    <h3 style={styles.modalTitle}>
                                        <i className="fas fa-trash-alt me-2"></i>
                                        Delete Manager
                                    </h3>
                                    <button
                                        className="modal-close"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setUserToDelete(null);
                                        }}
                                        style={styles.modalClose}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                                <div className="modal-body" style={styles.modalBody}>
                                    <div className="warning-alert" style={styles.warningAlert}>
                                        <div className="alert-icon" style={styles.warningAlertIcon}>
                                            <i className="fas fa-exclamation-triangle"></i>
                                        </div>
                                        <div className="alert-message" style={styles.warningAlertMessage}>
                                            <strong>Warning: This action cannot be undone!</strong>
                                            <span>Are you sure you want to delete manager: <strong>{userToDelete.name}</strong>?</span>
                                            <span style={{fontSize: '0.9rem', marginTop: '5px', color: 'rgba(255, 255, 255, 0.8)'}}>
                                                This will permanently delete all data associated with this manager.
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="user-details-preview" style={styles.userDetailsPreview}>
                                        <div style={styles.userDetailItem}>
                                            <span style={styles.userDetailLabel}>Manager ID:</span>
                                            <span style={styles.userDetailValue}>#{userToDelete.id}</span>
                                        </div>
                                        <div style={styles.userDetailItem}>
                                            <span style={styles.userDetailLabel}>Email:</span>
                                            <span style={styles.userDetailValue}>{userToDelete.email}</span>
                                        </div>
                                        <div style={styles.userDetailItem}>
                                            <span style={styles.userDetailLabel}>Status:</span>
                                            <span style={styles.userDetailValue}>
                                                <span style={getStatusBadgeStyle(userToDelete.status)}>
                                                    {userToDelete.status}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer" style={styles.modalFooter}>
                                    <button
                                        className="cancel-btn"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setUserToDelete(null);
                                        }}
                                        disabled={deletingUser === userToDelete.id}
                                        style={styles.cancelBtn}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="delete-confirm-btn"
                                        onClick={confirmDeleteUser}
                                        disabled={deletingUser === userToDelete.id}
                                        style={{
                                            ...styles.deleteConfirmBtn,
                                            background: `linear-gradient(135deg, ${colors.danger}, #dc2626)`,
                                            opacity: deletingUser === userToDelete.id ? 0.6 : 1,
                                            cursor: deletingUser === userToDelete.id ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {deletingUser === userToDelete.id ? (
                                            <>
                                                <span className="spinner" style={styles.spinner}></span>
                                                Deleting...
                                            </>
                                        ) : (
                                            'Confirm Delete'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Password Update Modal */}
                {showPasswordModal && selectedUser && (
                    <div className="modal-overlay" style={styles.modalOverlay}>
                        <div className="modal-container" style={styles.modalContainer}>
                            <div className="modal-card" style={styles.modalCard}>
                                <div className="modal-header" style={styles.modalHeader}>
                                    <h3 style={styles.modalTitle}>
                                        <i className="fas fa-key me-2"></i>
                                        Update Manager Password
                                    </h3>
                                    <button
                                        className="modal-close"
                                        onClick={() => setShowPasswordModal(false)}
                                        style={styles.modalClose}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                                <div className="modal-body" style={styles.modalBody}>
                                    <div className="info-alert" style={styles.infoAlert}>
                                        <div className="alert-icon" style={styles.infoAlertIcon}>
                                            <i className="fas fa-info-circle"></i>
                                        </div>
                                        <div className="alert-message" style={styles.infoAlertMessage}>
                                            <strong>Updating password for:</strong>
                                            <span>{selectedUser?.name}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="form-group" style={styles.formGroup}>
                                        <label style={styles.formLabel}>New Password</label>
                                        <input
                                            type="password"
                                            style={styles.formInput}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            placeholder="Enter new password"
                                            minLength="6"
                                        />
                                        <div style={styles.formHelp}>
                                            Password must be at least 6 characters long
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer" style={styles.modalFooter}>
                                    <button
                                        className="cancel-btn"
                                        onClick={() => setShowPasswordModal(false)}
                                        disabled={updating}
                                        style={styles.cancelBtn}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="update-btn"
                                        onClick={handleUpdatePassword}
                                        disabled={updating || newPassword.length < 6}
                                        style={{
                                            ...styles.updateBtn,
                                            opacity: (updating || newPassword.length < 6) ? 0.6 : 1,
                                            cursor: (updating || !newPassword) ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {updating ? (
                                            <>
                                                <span className="spinner" style={styles.spinner}></span>
                                                Updating...
                                            </>
                                        ) : (
                                            'Update Password'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .spinning {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes float {
                    0%, 100% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                }
            `}</style>
        </Layout>
    );
}

/* --- STYLES OBJECT --- */
const styles = {
    container: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.secondary,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    orb3: {
        width: '250px',
        height: '250px',
        background: colors.accent,
        bottom: '20%',
        left: '15%',
        animationDelay: '4s'
    },
    orb4: {
        width: '180px',
        height: '180px',
        background: colors.success,
        top: '30%',
        right: '20%',
        animationDelay: '6s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 30px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    greeting: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '15px'
    },
    pageTitle: {
        fontSize: '3rem',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: 0
    },
    welcomeBadge: {
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '8px 16px',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    badgeText: {
        color: 'white',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    badgeRole: {
        background: colors.primary,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    pageSubtitle: {
        color: colors.textLight,
        fontSize: '1.2rem',
        margin: 0,
        maxWidth: '600px'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '30px'
    },
    clientCount: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '10px 16px',
        borderRadius: '15px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)'
    },
    clientCountText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    clientCountNumber: {
        background: colors.primary,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontWeight: '700',
        fontSize: '0.9rem'
    },
    refreshBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '12px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    errorAlert: {
        background: `rgba(${parseInt(colors.danger.slice(1, 3), 16)}, ${parseInt(colors.danger.slice(3, 5), 16)}, ${parseInt(colors.danger.slice(5, 7), 16)}, 0.1)`,
        color: colors.danger,
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${colors.danger}80`,
        maxWidth: '1400px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    successAlert: {
        background: `rgba(${parseInt(colors.success.slice(1, 3), 16)}, ${parseInt(colors.success.slice(3, 5), 16)}, ${parseInt(colors.success.slice(5, 7), 16)}, 0.1)`,
        color: colors.success,
        padding: '20px',
        borderRadius: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${colors.success}`
    },
    alertIcon: {
        fontSize: '1.5rem',
        color: colors.danger
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        color: 'white'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: colors.danger,
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    // ✅ ADDED: Warning alert style for delete modal
    warningAlert: {
        background: `rgba(${parseInt(colors.danger.slice(1, 3), 16)}, ${parseInt(colors.danger.slice(3, 5), 16)}, ${parseInt(colors.danger.slice(5, 7), 16)}, 0.1)`,
        border: `2px solid ${colors.danger}`,
        borderRadius: '15px',
        padding: '20px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '15px',
        marginBottom: '20px'
    },
    warningAlertIcon: {
        color: colors.danger,
        fontSize: '1.5rem',
        marginTop: '2px'
    },
    warningAlertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        color: 'white'
    },
    // ✅ ADDED: User details preview for delete modal
    userDetailsPreview: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '15px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    userDetailItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        paddingBottom: '10px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
    },
    userDetailLabel: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontWeight: '600',
        minWidth: '80px',
        fontSize: '0.9rem'
    },
    userDetailValue: {
        color: 'white',
        fontWeight: '500',
        fontSize: '0.9rem',
        marginLeft: '10px'
    },
    // ✅ ADDED: Delete confirm button style
    deleteConfirmBtn: {
        background: colors.danger,
        color: 'white',
        border: 'none',
        padding: '12px 25px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: '140px',
        justifyContent: 'center'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    clientsTableSection: {
        marginBottom: '40px'
    },
    tableCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden'
    },
    tableCardHeader: {
        background: colors.gradientMain,
        color: 'white',
        padding: '25px 30px'
    },
    tableCardTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        margin: 0
    },
    tableCardBody: {
        padding: '0'
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
        color: 'white',
        textAlign: 'center'
    },
    loadingDots: {
        display: 'flex',
        gap: '8px'
    },
    dot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: 'white',
        animation: 'bounce 1.4s ease-in-out infinite both'
    },
    loadingText: {
        fontSize: '1.8rem',
        margin: '0 0 12px 0',
        fontWeight: '600'
    },
    loadingSubtext: {
        color: colors.textLight,
        margin: 0,
        fontSize: '1.1rem'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 40px',
        color: colors.textLight
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: '20px',
        opacity: 0.5
    },
    emptyTitle: {
        color: 'white',
        marginBottom: '10px'
    },
    tableContainer: {
        overflowX: 'auto'
    },
    clientsTable: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    tableHeader: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        padding: '15px 12px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.85rem',
        borderBottom: `2px solid ${colors.borderDark}`,
        whiteSpace: 'nowrap'
    },
    tableRow: {
        borderBottom: `1px solid ${colors.borderDark}`,
        transition: 'background 0.3s ease'
    },
    tableCell: {
        padding: '12px',
        color: 'white',
        fontSize: '0.85rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
    },
    userId: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    userAvatar: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        background: colors.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '0.9rem'
    },
    userName: {
        fontWeight: '600',
        color: 'white'
    },
    userEmail: {
        color: colors.textLight,
        fontWeight: '500'
    },
    descriptionText: {
        color: colors.textLight,
        fontSize: '0.8rem',
        wordBreak: 'break-word',
    },
    actionButtons: {
        display: 'flex',
        gap: '8px'
    },
    spinner: {
        width: '12px',
        height: '12px',
        border: '2px solid transparent',
        borderTop: '2px solid currentColor',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    tableFooter: {
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '15px 30px',
        borderTop: `1px solid ${colors.borderDark}`
    },
    footerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    footerText: {
        color: colors.textLight,
        fontSize: '0.85rem',
        fontWeight: '500'
    },
    // MODAL STYLES (for Detail and Password)
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
    },
    modalContainer: {
        width: '100%',
        maxWidth: '500px'
    },
    modalCard: {
        background: colors.backgroundMedium,
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.8)',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        background: colors.gradientMain,
        color: 'white',
        padding: '25px 30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    modalTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        margin: 0
    },
    modalClose: {
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        fontSize: '1.2rem',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    modalBody: {
        padding: '30px',
        maxHeight: '60vh',
        overflowY: 'auto'
    },
    infoAlert: {
        background: 'rgba(6, 182, 212, 0.1)',
        border: `1px solid ${colors.accent}`,
        borderRadius: '12px',
        padding: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
    },
    infoAlertIcon: {
        color: colors.accent,
        fontSize: '1.2rem'
    },
    infoAlertMessage: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    formGroup: {
        marginBottom: '20px'
    },
    formLabel: {
        display: 'block',
        color: 'white',
        fontWeight: '600',
        marginBottom: '8px',
        fontSize: '0.9rem'
    },
    formInput: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${colors.borderDark}`,
        borderRadius: '10px',
        padding: '12px 15px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        resize: 'none'
    },
    formHelp: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.8rem',
        marginTop: '8px'
    },
    modalFooter: {
        padding: '20px 30px',
        borderTop: `1px solid ${colors.borderDark}`,
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        background: 'rgba(255, 255, 255, 0.05)',
        position: 'sticky',
        bottom: 0,
        zIndex: 10
    },
    cancelBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '10px 20px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        minWidth: '100px'
    },
    updateBtn: {
        background: colors.gradientMain,
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: '140px',
        justifyContent: 'center'
    }
};

export default ModifyManagers;